/**
 * FilterBar - Filter controls for Matches page
 *
 * Features:
 * - Time range filter (today, week, month, all)
 * - Event type filter (multi-select)
 * - Assignment filter (mine, unassigned, all)
 * - Status filter (upcoming, live, finished)
 * - Role-based filter visibility
 */

import { useMemo } from 'react';
import clsx from 'clsx';
import Button from '../../../../shared/components/ui/Button';
import { EVENT_TYPES, SPORTS } from '../../utils/constants';
import styles from './FilterBar.module.css';

/**
 * Time range options
 */
const TIME_RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'all', label: 'All' },
];

/**
 * Status options
 */
const STATUS_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'live', label: 'Live' },
  { key: 'finished', label: 'Finished' },
];

/**
 * Assignment options (for planners/managers)
 */
const ASSIGNMENT_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'mine', label: 'My Assignments' },
  { key: 'unassigned', label: 'Unassigned' },
];

/**
 * Pill button component for filter options
 */
function FilterPill({ active, onClick, children, color }) {
  return (
    <button
      className={clsx(styles.pill, active && styles.pillActive)}
      onClick={onClick}
      style={active && color ? { borderColor: color, color } : undefined}
    >
      {children}
    </button>
  );
}

/**
 * Filter section with label
 */
function FilterSection({ label, children }) {
  return (
    <div className={styles.section}>
      <span className={styles.sectionLabel}>{label}</span>
      <div className={styles.sectionOptions}>{children}</div>
    </div>
  );
}

/**
 * Multi-select dropdown for event types
 */
function EventTypeSelect({ selected, onChange }) {
  const eventTypes = useMemo(
    () => Object.entries(EVENT_TYPES).map(([key, val]) => ({ key, ...val })),
    []
  );

  const toggleType = (key) => {
    if (selected.includes(key)) {
      onChange(selected.filter((k) => k !== key));
    } else {
      onChange([...selected, key]);
    }
  };

  const allSelected = selected.length === 0;

  return (
    <div className={styles.eventTypes}>
      <FilterPill active={allSelected} onClick={() => onChange([])}>
        All Types
      </FilterPill>
      {eventTypes.map((type) => (
        <FilterPill
          key={type.key}
          active={selected.includes(type.key)}
          onClick={() => toggleType(type.key)}
          color={type.color}
        >
          {type.label}
        </FilterPill>
      ))}
    </div>
  );
}

/**
 * Sport filter dropdown
 */
function SportSelect({ value, onChange }) {
  const sports = useMemo(
    () => Object.entries(SPORTS).map(([key, val]) => ({ key, ...val })),
    []
  );

  return (
    <select
      className={styles.select}
      value={value || ''}
      onChange={(e) => onChange(e.target.value || null)}
    >
      <option value="">All Sports</option>
      {sports.map((sport) => (
        <option key={sport.key} value={sport.key}>
          {sport.label}
        </option>
      ))}
    </select>
  );
}

/**
 * Main FilterBar component
 */
export default function FilterBar({
  filters,
  onChange,
  onClear,
  onCreateMatch,
  canCreate = false,
  role = 'viewer',
  matchCount = 0,
}) {
  const {
    timeRange = 'week',
    status = 'all',
    assignment = 'all',
    eventTypes = [],
    sport = null,
    search = '',
  } = filters;

  const updateFilter = (key, value) => {
    onChange({ ...filters, [key]: value });
  };

  const hasActiveFilters =
    timeRange !== 'week' ||
    status !== 'all' ||
    assignment !== 'all' ||
    eventTypes.length > 0 ||
    sport !== null ||
    search !== '';

  // Role-based filter visibility
  const showAssignmentFilter = role === 'COMPANY_MANAGER' || role === 'PLANNER';

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        {/* Search */}
        <div className={styles.searchWrapper}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search matches..."
            value={search}
            onChange={(e) => updateFilter('search', e.target.value)}
          />
          {search && (
            <button
              className={styles.clearSearch}
              onClick={() => updateFilter('search', '')}
              aria-label="Clear search"
            >
              x
            </button>
          )}
        </div>

        {/* Time range */}
        <FilterSection label="Time">
          {TIME_RANGES.map((range) => (
            <FilterPill
              key={range.key}
              active={timeRange === range.key}
              onClick={() => updateFilter('timeRange', range.key)}
            >
              {range.label}
            </FilterPill>
          ))}
        </FilterSection>

        {/* Status */}
        <FilterSection label="Status">
          {STATUS_OPTIONS.map((opt) => (
            <FilterPill
              key={opt.key}
              active={status === opt.key}
              onClick={() => updateFilter('status', opt.key)}
            >
              {opt.label}
            </FilterPill>
          ))}
        </FilterSection>

        {/* Assignment filter (role-based) */}
        {showAssignmentFilter && (
          <FilterSection label="Assigned">
            {ASSIGNMENT_OPTIONS.map((opt) => (
              <FilterPill
                key={opt.key}
                active={assignment === opt.key}
                onClick={() => updateFilter('assignment', opt.key)}
              >
                {opt.label}
              </FilterPill>
            ))}
          </FilterSection>
        )}

        {/* Sport dropdown */}
        <SportSelect value={sport} onChange={(val) => updateFilter('sport', val)} />
      </div>

      <div className={styles.row}>
        {/* Event types */}
        <EventTypeSelect
          selected={eventTypes}
          onChange={(val) => updateFilter('eventTypes', val)}
        />

        <div className={styles.spacer} />

        {/* Match count */}
        <span className={styles.count}>
          {matchCount} {matchCount === 1 ? 'match' : 'matches'}
        </span>

        {/* Clear filters */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={onClear}>
            Clear Filters
          </Button>
        )}

        {/* Create match button */}
        {canCreate && (
          <Button variant="primary" size="sm" onClick={onCreateMatch}>
            + New Match
          </Button>
        )}
      </div>
    </div>
  );
}
